export type SupportedLang =
  | 'en' | 'fr' | 'de' | 'nl' | 'es' | 'pt' | 'ko' | 'ja' | 'zh' | 'hi' | 'ar'
  | 'bn' | 'ru' | 'id' | 'vi' | 'tr' | 'it' | 'th' | 'pl' | 'uk' | 'ms' | 'cs' | 'sv' | 'da' | 'is';

export interface WidgetTranslations {
  title: string;
  subtitle: string;
  placeholder: string;
  welcomeMessage: string;
  errorPrefix: string;
  connectionError: string;
  streamingNotSupported: string;
  poweredBy: string;
  sourcesLabel: string;
  // Booking translations
  bookingTitle: string;
  selectDate: string;
  selectTime: string;
  bookAppointment: string;
  noSlotsAvailable: string;
  loadingSlots: string;
  bookingSuccess: string;
  bookingError: string;
  backToChat: string;
  yourDetails: string;
  confirmBooking: string;
  meetingWith: string;
  duration: string;
  minutes: string;
  joinMeeting: string;
}

export const WIDGET_TRANSLATIONS: Record<SupportedLang, WidgetTranslations> = {
  en: {
    title: 'Libre Bot',
    subtitle: 'AI Documentation Assistant',
    placeholder: 'Ask a question...',
    welcomeMessage: 'Hi! I can help you find answers in the documentation. What would you like to know?',
    errorPrefix: 'Sorry, I encountered an error:',
    connectionError: 'Sorry, I had trouble connecting. Please try again.',
    streamingNotSupported: 'Sorry, streaming is not supported.',
    poweredBy: 'Powered by',
    sourcesLabel: 'Sources:',
    bookingTitle: 'Book an Appointment',
    selectDate: 'Select a date',
    selectTime: 'Select a time',
    bookAppointment: 'Book Appointment',
    noSlotsAvailable: 'No time slots available for this date',
    loadingSlots: 'Loading available times...',
    bookingSuccess: 'Your appointment has been confirmed!',
    bookingError: 'Failed to book appointment. Please try again.',
    backToChat: 'Back to chat',
    yourDetails: 'Your Details',
    confirmBooking: 'Confirm Booking',
    meetingWith: 'Meeting',
    duration: 'Duration',
    minutes: 'minutes',
    joinMeeting: 'Join Meeting',
  },
  fr: {
    title: 'Libre Bot',
    subtitle: 'Assistant IA de documentation',
    placeholder: 'Posez une question...',
    welcomeMessage: 'Bonjour ! Je peux vous aider à trouver des réponses dans la documentation. Que souhaitez-vous savoir ?',
    errorPrefix: 'Désolé, j\'ai rencontré une erreur :',
    connectionError: 'Désolé, j\'ai eu du mal à me connecter. Veuillez réessayer.',
    streamingNotSupported: 'Désolé, le streaming n\'est pas pris en charge.',
    poweredBy: 'Propulsé par',
    sourcesLabel: 'Sources :',
    bookingTitle: 'Prendre rendez-vous',
    selectDate: 'Choisir une date',
    selectTime: 'Choisir une heure',
    bookAppointment: 'Prendre rendez-vous',
    noSlotsAvailable: 'Aucun créneau disponible pour cette date',
    loadingSlots: 'Chargement des horaires...',
    bookingSuccess: 'Votre rendez-vous a été confirmé !',
    bookingError: 'Échec de la réservation. Veuillez réessayer.',
    backToChat: 'Retour au chat',
    yourDetails: 'Vos informations',
    confirmBooking: 'Confirmer la réservation',
    meetingWith: 'Réunion',
    duration: 'Durée',
    minutes: 'minutes',
    joinMeeting: 'Rejoindre la réunion',
  },
  de: {
    title: 'Libre Bot',
    subtitle: 'KI-Dokumentationsassistent',
    placeholder: 'Stellen Sie eine Frage...',
    welcomeMessage: 'Hallo! Ich kann Ihnen helfen, Antworten in der Dokumentation zu finden. Was möchten Sie wissen?',
    errorPrefix: 'Entschuldigung, ein Fehler ist aufgetreten:',
    connectionError: 'Entschuldigung, ich hatte Verbindungsprobleme. Bitte versuchen Sie es erneut.',
    streamingNotSupported: 'Entschuldigung, Streaming wird nicht unterstützt.',
    poweredBy: 'Betrieben von',
    sourcesLabel: 'Quellen:',
    bookingTitle: 'Termin buchen',
    selectDate: 'Datum auswählen',
    selectTime: 'Uhrzeit auswählen',
    bookAppointment: 'Termin buchen',
    noSlotsAvailable: 'Keine Termine für dieses Datum verfügbar',
    loadingSlots: 'Verfügbare Zeiten werden geladen...',
    bookingSuccess: 'Ihr Termin wurde bestätigt!',
    bookingError: 'Terminbuchung fehlgeschlagen. Bitte versuchen Sie es erneut.',
    backToChat: 'Zurück zum Chat',
    yourDetails: 'Ihre Angaben',
    confirmBooking: 'Buchung bestätigen',
    meetingWith: 'Meeting',
    duration: 'Dauer',
    minutes: 'Minuten',
    joinMeeting: 'An Meeting teilnehmen',
  },
  nl: {
    title: 'Libre Bot',
    subtitle: 'AI-documentatieassistent',
    placeholder: 'Stel een vraag...',
    welcomeMessage: 'Hallo! Ik kan je helpen antwoorden te vinden in de documentatie. Wat wil je weten?',
    errorPrefix: 'Sorry, er is een fout opgetreden:',
    connectionError: 'Sorry, ik had moeite met verbinden. Probeer het opnieuw.',
    streamingNotSupported: 'Sorry, streaming wordt niet ondersteund.',
    poweredBy: 'Mogelijk gemaakt door',
    sourcesLabel: 'Bronnen:',
    bookingTitle: 'Afspraak maken',
    selectDate: 'Selecteer een datum',
    selectTime: 'Selecteer een tijd',
    bookAppointment: 'Afspraak maken',
    noSlotsAvailable: 'Geen tijdsloten beschikbaar voor deze datum',
    loadingSlots: 'Beschikbare tijden laden...',
    bookingSuccess: 'Je afspraak is bevestigd!',
    bookingError: 'Afspraak maken mislukt. Probeer het opnieuw.',
    backToChat: 'Terug naar chat',
    yourDetails: 'Je gegevens',
    confirmBooking: 'Boeking bevestigen',
    meetingWith: 'Vergadering',
    duration: 'Duur',
    minutes: 'minuten',
    joinMeeting: 'Deelnemen aan vergadering',
  },
  es: {
    title: 'Libre Bot',
    subtitle: 'Asistente de documentación con IA',
    placeholder: 'Haz una pregunta...',
    welcomeMessage: '¡Hola! Puedo ayudarte a encontrar respuestas en la documentación. ¿Qué te gustaría saber?',
    errorPrefix: 'Lo siento, encontré un error:',
    connectionError: 'Lo siento, tuve problemas para conectarme. Por favor, inténtalo de nuevo.',
    streamingNotSupported: 'Lo siento, el streaming no está soportado.',
    poweredBy: 'Desarrollado por',
    sourcesLabel: 'Fuentes:',
    bookingTitle: 'Reservar cita',
    selectDate: 'Seleccionar fecha',
    selectTime: 'Seleccionar hora',
    bookAppointment: 'Reservar cita',
    noSlotsAvailable: 'No hay horarios disponibles para esta fecha',
    loadingSlots: 'Cargando horarios disponibles...',
    bookingSuccess: '¡Tu cita ha sido confirmada!',
    bookingError: 'Error al reservar la cita. Por favor, inténtalo de nuevo.',
    backToChat: 'Volver al chat',
    yourDetails: 'Tus datos',
    confirmBooking: 'Confirmar reserva',
    meetingWith: 'Reunión',
    duration: 'Duración',
    minutes: 'minutos',
    joinMeeting: 'Unirse a la reunión',
  },
  pt: {
    title: 'Libre Bot',
    subtitle: 'Assistente de documentação com IA',
    placeholder: 'Faça uma pergunta...',
    welcomeMessage: 'Olá! Posso ajudá-lo a encontrar respostas na documentação. O que você gostaria de saber?',
    errorPrefix: 'Desculpe, encontrei um erro:',
    connectionError: 'Desculpe, tive problemas para conectar. Por favor, tente novamente.',
    streamingNotSupported: 'Desculpe, streaming não é suportado.',
    poweredBy: 'Desenvolvido por',
    sourcesLabel: 'Fontes:',
    bookingTitle: 'Agendar consulta',
    selectDate: 'Selecionar data',
    selectTime: 'Selecionar horário',
    bookAppointment: 'Agendar consulta',
    noSlotsAvailable: 'Nenhum horário disponível para esta data',
    loadingSlots: 'Carregando horários disponíveis...',
    bookingSuccess: 'Seu agendamento foi confirmado!',
    bookingError: 'Falha ao agendar. Por favor, tente novamente.',
    backToChat: 'Voltar ao chat',
    yourDetails: 'Seus dados',
    confirmBooking: 'Confirmar agendamento',
    meetingWith: 'Reunião',
    duration: 'Duração',
    minutes: 'minutos',
    joinMeeting: 'Entrar na reunião',
  },
  ko: {
    title: 'Libre Bot',
    subtitle: 'AI 문서 도우미',
    placeholder: '질문하세요...',
    welcomeMessage: '안녕하세요! 문서에서 답변을 찾는 데 도움을 드릴 수 있습니다. 무엇을 알고 싶으신가요?',
    errorPrefix: '죄송합니다. 오류가 발생했습니다:',
    connectionError: '죄송합니다. 연결에 문제가 있습니다. 다시 시도해 주세요.',
    streamingNotSupported: '죄송합니다. 스트리밍이 지원되지 않습니다.',
    poweredBy: '제공:',
    sourcesLabel: '출처:',
    bookingTitle: '예약하기',
    selectDate: '날짜 선택',
    selectTime: '시간 선택',
    bookAppointment: '예약하기',
    noSlotsAvailable: '선택한 날짜에 가능한 시간이 없습니다',
    loadingSlots: '가능한 시간 로딩 중...',
    bookingSuccess: '예약이 확정되었습니다!',
    bookingError: '예약에 실패했습니다. 다시 시도해 주세요.',
    backToChat: '채팅으로 돌아가기',
    yourDetails: '정보 입력',
    confirmBooking: '예약 확정',
    meetingWith: '미팅',
    duration: '소요 시간',
    minutes: '분',
    joinMeeting: '미팅 참가',
  },
  ja: {
    title: 'Libre Bot',
    subtitle: 'AIドキュメントアシスタント',
    placeholder: '質問してください...',
    welcomeMessage: 'こんにちは！ドキュメントから回答を見つけるお手伝いができます。何をお知りになりたいですか？',
    errorPrefix: '申し訳ありません。エラーが発生しました：',
    connectionError: '申し訳ありません。接続に問題がありました。もう一度お試しください。',
    streamingNotSupported: '申し訳ありません。ストリーミングはサポートされていません。',
    poweredBy: '提供：',
    sourcesLabel: '出典：',
    bookingTitle: '予約する',
    selectDate: '日付を選択',
    selectTime: '時間を選択',
    bookAppointment: '予約する',
    noSlotsAvailable: 'この日は空き時間がありません',
    loadingSlots: '空き時間を読み込み中...',
    bookingSuccess: 'ご予約が確定しました！',
    bookingError: '予約に失敗しました。もう一度お試しください。',
    backToChat: 'チャットに戻る',
    yourDetails: 'お客様情報',
    confirmBooking: '予約を確定',
    meetingWith: 'ミーティング',
    duration: '所要時間',
    minutes: '分',
    joinMeeting: 'ミーティングに参加',
  },
  zh: {
    title: 'Libre Bot',
    subtitle: 'AI文档助手',
    placeholder: '请提问...',
    welcomeMessage: '您好！我可以帮助您在文档中查找答案。您想了解什么？',
    errorPrefix: '抱歉，遇到了错误：',
    connectionError: '抱歉，连接出现问题。请重试。',
    streamingNotSupported: '抱歉，不支持流式传输。',
    poweredBy: '由以下提供支持：',
    sourcesLabel: '来源：',
    bookingTitle: '预约',
    selectDate: '选择日期',
    selectTime: '选择时间',
    bookAppointment: '预约',
    noSlotsAvailable: '该日期没有可用时间段',
    loadingSlots: '加载可用时间...',
    bookingSuccess: '您的预约已确认！',
    bookingError: '预约失败。请重试。',
    backToChat: '返回聊天',
    yourDetails: '您的信息',
    confirmBooking: '确认预约',
    meetingWith: '会议',
    duration: '时长',
    minutes: '分钟',
    joinMeeting: '加入会议',
  },
  hi: {
    title: 'Libre Bot',
    subtitle: 'AI दस्तावेज़ सहायक',
    placeholder: 'कोई प्रश्न पूछें...',
    welcomeMessage: 'नमस्ते! मैं आपको दस्तावेज़ों में उत्तर खोजने में मदद कर सकता हूं। आप क्या जानना चाहते हैं?',
    errorPrefix: 'क्षमा करें, एक त्रुटि हुई:',
    connectionError: 'क्षमा करें, कनेक्ट करने में समस्या हुई। कृपया पुनः प्रयास करें।',
    streamingNotSupported: 'क्षमा करें, स्ट्रीमिंग समर्थित नहीं है।',
    poweredBy: 'द्वारा संचालित',
    sourcesLabel: 'स्रोत:',
    bookingTitle: 'अपॉइंटमेंट बुक करें',
    selectDate: 'तिथि चुनें',
    selectTime: 'समय चुनें',
    bookAppointment: 'अपॉइंटमेंट बुक करें',
    noSlotsAvailable: 'इस तिथि के लिए कोई समय उपलब्ध नहीं है',
    loadingSlots: 'उपलब्ध समय लोड हो रहा है...',
    bookingSuccess: 'आपकी अपॉइंटमेंट की पुष्टि हो गई है!',
    bookingError: 'बुकिंग विफल। कृपया पुनः प्रयास करें।',
    backToChat: 'चैट पर वापस जाएं',
    yourDetails: 'आपका विवरण',
    confirmBooking: 'बुकिंग की पुष्टि करें',
    meetingWith: 'मीटिंग',
    duration: 'अवधि',
    minutes: 'मिनट',
    joinMeeting: 'मीटिंग में शामिल हों',
  },
  ar: {
    title: 'Libre Bot',
    subtitle: 'مساعد الوثائق بالذكاء الاصطناعي',
    placeholder: 'اطرح سؤالاً...',
    welcomeMessage: 'مرحباً! يمكنني مساعدتك في العثور على إجابات في الوثائق. ماذا تريد أن تعرف؟',
    errorPrefix: 'عذراً، حدث خطأ:',
    connectionError: 'عذراً، واجهت مشكلة في الاتصال. يرجى المحاولة مرة أخرى.',
    streamingNotSupported: 'عذراً، البث غير مدعوم.',
    poweredBy: 'مدعوم من',
    sourcesLabel: 'المصادر:',
    bookingTitle: 'حجز موعد',
    selectDate: 'اختر التاريخ',
    selectTime: 'اختر الوقت',
    bookAppointment: 'حجز موعد',
    noSlotsAvailable: 'لا توجد أوقات متاحة لهذا التاريخ',
    loadingSlots: 'جاري تحميل الأوقات المتاحة...',
    bookingSuccess: 'تم تأكيد موعدك!',
    bookingError: 'فشل الحجز. يرجى المحاولة مرة أخرى.',
    backToChat: 'العودة إلى المحادثة',
    yourDetails: 'بياناتك',
    confirmBooking: 'تأكيد الحجز',
    meetingWith: 'اجتماع',
    duration: 'المدة',
    minutes: 'دقيقة',
    joinMeeting: 'انضم إلى الاجتماع',
  },
  // Bengali (বাংলা) - ~270M speakers
  bn: {
    title: 'Libre Bot',
    subtitle: 'AI ডকুমেন্টেশন সহায়ক',
    placeholder: 'একটি প্রশ্ন জিজ্ঞাসা করুন...',
    welcomeMessage: 'হ্যালো! আমি আপনাকে ডকুমেন্টেশনে উত্তর খুঁজে পেতে সাহায্য করতে পারি। আপনি কী জানতে চান?',
    errorPrefix: 'দুঃখিত, একটি ত্রুটি ঘটেছে:',
    connectionError: 'দুঃখিত, সংযোগে সমস্যা হয়েছে। অনুগ্রহ করে আবার চেষ্টা করুন।',
    streamingNotSupported: 'দুঃখিত, স্ট্রিমিং সমর্থিত নয়।',
    poweredBy: 'দ্বারা চালিত',
    sourcesLabel: 'সূত্র:',
    bookingTitle: 'অ্যাপয়েন্টমেন্ট বুক করুন',
    selectDate: 'তারিখ নির্বাচন করুন',
    selectTime: 'সময় নির্বাচন করুন',
    bookAppointment: 'অ্যাপয়েন্টমেন্ট বুক করুন',
    noSlotsAvailable: 'এই তারিখে কোনো সময় উপলব্ধ নেই',
    loadingSlots: 'উপলব্ধ সময় লোড হচ্ছে...',
    bookingSuccess: 'আপনার অ্যাপয়েন্টমেন্ট নিশ্চিত হয়েছে!',
    bookingError: 'বুকিং ব্যর্থ হয়েছে। অনুগ্রহ করে আবার চেষ্টা করুন।',
    backToChat: 'চ্যাটে ফিরে যান',
    yourDetails: 'আপনার তথ্য',
    confirmBooking: 'বুকিং নিশ্চিত করুন',
    meetingWith: 'মিটিং',
    duration: 'সময়কাল',
    minutes: 'মিনিট',
    joinMeeting: 'মিটিংয়ে যোগ দিন',
  },
  // Russian (Русский) - ~255M speakers
  ru: {
    title: 'Libre Bot',
    subtitle: 'ИИ-ассистент по документации',
    placeholder: 'Задайте вопрос...',
    welcomeMessage: 'Привет! Я могу помочь вам найти ответы в документации. Что бы вы хотели узнать?',
    errorPrefix: 'Извините, произошла ошибка:',
    connectionError: 'Извините, возникла проблема с подключением. Пожалуйста, попробуйте снова.',
    streamingNotSupported: 'Извините, потоковая передача не поддерживается.',
    poweredBy: 'Работает на',
    sourcesLabel: 'Источники:',
    bookingTitle: 'Записаться на приём',
    selectDate: 'Выберите дату',
    selectTime: 'Выберите время',
    bookAppointment: 'Записаться',
    noSlotsAvailable: 'На эту дату нет свободного времени',
    loadingSlots: 'Загрузка доступного времени...',
    bookingSuccess: 'Ваша запись подтверждена!',
    bookingError: 'Не удалось записаться. Пожалуйста, попробуйте снова.',
    backToChat: 'Вернуться к чату',
    yourDetails: 'Ваши данные',
    confirmBooking: 'Подтвердить запись',
    meetingWith: 'Встреча',
    duration: 'Продолжительность',
    minutes: 'минут',
    joinMeeting: 'Присоединиться к встрече',
  },
  // Indonesian (Bahasa Indonesia) - ~200M speakers
  id: {
    title: 'Libre Bot',
    subtitle: 'Asisten Dokumentasi AI',
    placeholder: 'Ajukan pertanyaan...',
    welcomeMessage: 'Halo! Saya dapat membantu Anda menemukan jawaban di dokumentasi. Apa yang ingin Anda ketahui?',
    errorPrefix: 'Maaf, terjadi kesalahan:',
    connectionError: 'Maaf, ada masalah koneksi. Silakan coba lagi.',
    streamingNotSupported: 'Maaf, streaming tidak didukung.',
    poweredBy: 'Didukung oleh',
    sourcesLabel: 'Sumber:',
    bookingTitle: 'Buat Janji',
    selectDate: 'Pilih tanggal',
    selectTime: 'Pilih waktu',
    bookAppointment: 'Buat Janji',
    noSlotsAvailable: 'Tidak ada waktu tersedia untuk tanggal ini',
    loadingSlots: 'Memuat waktu tersedia...',
    bookingSuccess: 'Janji Anda telah dikonfirmasi!',
    bookingError: 'Gagal membuat janji. Silakan coba lagi.',
    backToChat: 'Kembali ke chat',
    yourDetails: 'Data Anda',
    confirmBooking: 'Konfirmasi Pemesanan',
    meetingWith: 'Pertemuan',
    duration: 'Durasi',
    minutes: 'menit',
    joinMeeting: 'Gabung Pertemuan',
  },
  // Vietnamese (Tiếng Việt) - ~85M speakers
  vi: {
    title: 'Libre Bot',
    subtitle: 'Trợ lý tài liệu AI',
    placeholder: 'Đặt câu hỏi...',
    welcomeMessage: 'Xin chào! Tôi có thể giúp bạn tìm câu trả lời trong tài liệu. Bạn muốn biết điều gì?',
    errorPrefix: 'Xin lỗi, đã xảy ra lỗi:',
    connectionError: 'Xin lỗi, có vấn đề kết nối. Vui lòng thử lại.',
    streamingNotSupported: 'Xin lỗi, không hỗ trợ phát trực tuyến.',
    poweredBy: 'Được cung cấp bởi',
    sourcesLabel: 'Nguồn:',
    bookingTitle: 'Đặt lịch hẹn',
    selectDate: 'Chọn ngày',
    selectTime: 'Chọn giờ',
    bookAppointment: 'Đặt lịch hẹn',
    noSlotsAvailable: 'Không có thời gian trống cho ngày này',
    loadingSlots: 'Đang tải thời gian trống...',
    bookingSuccess: 'Lịch hẹn của bạn đã được xác nhận!',
    bookingError: 'Đặt lịch thất bại. Vui lòng thử lại.',
    backToChat: 'Quay lại chat',
    yourDetails: 'Thông tin của bạn',
    confirmBooking: 'Xác nhận đặt lịch',
    meetingWith: 'Cuộc họp',
    duration: 'Thời lượng',
    minutes: 'phút',
    joinMeeting: 'Tham gia cuộc họp',
  },
  // Turkish (Türkçe) - ~80M speakers
  tr: {
    title: 'Libre Bot',
    subtitle: 'Yapay Zeka Dokümantasyon Asistanı',
    placeholder: 'Bir soru sorun...',
    welcomeMessage: 'Merhaba! Dokümantasyonda cevap bulmanıza yardımcı olabilirim. Ne öğrenmek istersiniz?',
    errorPrefix: 'Üzgünüm, bir hata oluştu:',
    connectionError: 'Üzgünüm, bağlantı sorunu yaşadım. Lütfen tekrar deneyin.',
    streamingNotSupported: 'Üzgünüm, akış desteklenmiyor.',
    poweredBy: 'Tarafından desteklenmektedir',
    sourcesLabel: 'Kaynaklar:',
    bookingTitle: 'Randevu Al',
    selectDate: 'Tarih seçin',
    selectTime: 'Saat seçin',
    bookAppointment: 'Randevu Al',
    noSlotsAvailable: 'Bu tarih için müsait saat yok',
    loadingSlots: 'Müsait saatler yükleniyor...',
    bookingSuccess: 'Randevunuz onaylandı!',
    bookingError: 'Randevu alınamadı. Lütfen tekrar deneyin.',
    backToChat: 'Sohbete dön',
    yourDetails: 'Bilgileriniz',
    confirmBooking: 'Randevuyu Onayla',
    meetingWith: 'Toplantı',
    duration: 'Süre',
    minutes: 'dakika',
    joinMeeting: 'Toplantıya Katıl',
  },
  // Italian (Italiano) - ~65M speakers
  it: {
    title: 'Libre Bot',
    subtitle: 'Assistente documentazione AI',
    placeholder: 'Fai una domanda...',
    welcomeMessage: 'Ciao! Posso aiutarti a trovare risposte nella documentazione. Cosa vorresti sapere?',
    errorPrefix: 'Mi dispiace, si è verificato un errore:',
    connectionError: 'Mi dispiace, ho avuto problemi di connessione. Per favore riprova.',
    streamingNotSupported: 'Mi dispiace, lo streaming non è supportato.',
    poweredBy: 'Offerto da',
    sourcesLabel: 'Fonti:',
    bookingTitle: 'Prenota un Appuntamento',
    selectDate: 'Seleziona una data',
    selectTime: 'Seleziona un orario',
    bookAppointment: 'Prenota Appuntamento',
    noSlotsAvailable: 'Nessun orario disponibile per questa data',
    loadingSlots: 'Caricamento orari disponibili...',
    bookingSuccess: 'Il tuo appuntamento è stato confermato!',
    bookingError: 'Prenotazione fallita. Per favore riprova.',
    backToChat: 'Torna alla chat',
    yourDetails: 'I tuoi dati',
    confirmBooking: 'Conferma Prenotazione',
    meetingWith: 'Riunione',
    duration: 'Durata',
    minutes: 'minuti',
    joinMeeting: 'Partecipa alla Riunione',
  },
  // Thai (ไทย) - ~60M speakers
  th: {
    title: 'Libre Bot',
    subtitle: 'ผู้ช่วยเอกสาร AI',
    placeholder: 'ถามคำถาม...',
    welcomeMessage: 'สวัสดี! ฉันสามารถช่วยคุณค้นหาคำตอบในเอกสารได้ คุณต้องการทราบอะไร?',
    errorPrefix: 'ขออภัย เกิดข้อผิดพลาด:',
    connectionError: 'ขออภัย มีปัญหาในการเชื่อมต่อ กรุณาลองอีกครั้ง',
    streamingNotSupported: 'ขออภัย ไม่รองรับการสตรีม',
    poweredBy: 'ขับเคลื่อนโดย',
    sourcesLabel: 'แหล่งที่มา:',
    bookingTitle: 'จองนัดหมาย',
    selectDate: 'เลือกวันที่',
    selectTime: 'เลือกเวลา',
    bookAppointment: 'จองนัดหมาย',
    noSlotsAvailable: 'ไม่มีช่วงเวลาว่างสำหรับวันนี้',
    loadingSlots: 'กำลังโหลดเวลาว่าง...',
    bookingSuccess: 'การนัดหมายของคุณได้รับการยืนยันแล้ว!',
    bookingError: 'การจองล้มเหลว กรุณาลองอีกครั้ง',
    backToChat: 'กลับไปที่แชท',
    yourDetails: 'ข้อมูลของคุณ',
    confirmBooking: 'ยืนยันการจอง',
    meetingWith: 'การประชุม',
    duration: 'ระยะเวลา',
    minutes: 'นาที',
    joinMeeting: 'เข้าร่วมการประชุม',
  },
  // Polish (Polski) - ~45M speakers
  pl: {
    title: 'Libre Bot',
    subtitle: 'Asystent dokumentacji AI',
    placeholder: 'Zadaj pytanie...',
    welcomeMessage: 'Cześć! Mogę pomóc Ci znaleźć odpowiedzi w dokumentacji. Co chciałbyś wiedzieć?',
    errorPrefix: 'Przepraszam, wystąpił błąd:',
    connectionError: 'Przepraszam, miałem problem z połączeniem. Spróbuj ponownie.',
    streamingNotSupported: 'Przepraszam, streaming nie jest obsługiwany.',
    poweredBy: 'Obsługiwane przez',
    sourcesLabel: 'Źródła:',
    bookingTitle: 'Umów wizytę',
    selectDate: 'Wybierz datę',
    selectTime: 'Wybierz godzinę',
    bookAppointment: 'Umów wizytę',
    noSlotsAvailable: 'Brak dostępnych terminów na ten dzień',
    loadingSlots: 'Ładowanie dostępnych terminów...',
    bookingSuccess: 'Twoja wizyta została potwierdzona!',
    bookingError: 'Nie udało się umówić wizyty. Spróbuj ponownie.',
    backToChat: 'Wróć do czatu',
    yourDetails: 'Twoje dane',
    confirmBooking: 'Potwierdź rezerwację',
    meetingWith: 'Spotkanie',
    duration: 'Czas trwania',
    minutes: 'minut',
    joinMeeting: 'Dołącz do spotkania',
  },
  // Ukrainian (Українська) - ~40M speakers
  uk: {
    title: 'Libre Bot',
    subtitle: 'ШІ-асистент документації',
    placeholder: 'Поставте запитання...',
    welcomeMessage: 'Привіт! Я можу допомогти вам знайти відповіді в документації. Що б ви хотіли дізнатися?',
    errorPrefix: 'Вибачте, сталася помилка:',
    connectionError: 'Вибачте, виникла проблема з підключенням. Будь ласка, спробуйте ще раз.',
    streamingNotSupported: 'Вибачте, потокова передача не підтримується.',
    poweredBy: 'Працює на',
    sourcesLabel: 'Джерела:',
    bookingTitle: 'Записатися на прийом',
    selectDate: 'Оберіть дату',
    selectTime: 'Оберіть час',
    bookAppointment: 'Записатися',
    noSlotsAvailable: 'На цю дату немає вільного часу',
    loadingSlots: 'Завантаження доступного часу...',
    bookingSuccess: 'Ваш запис підтверджено!',
    bookingError: 'Не вдалося записатися. Будь ласка, спробуйте ще раз.',
    backToChat: 'Повернутися до чату',
    yourDetails: 'Ваші дані',
    confirmBooking: 'Підтвердити запис',
    meetingWith: 'Зустріч',
    duration: 'Тривалість',
    minutes: 'хвилин',
    joinMeeting: 'Приєднатися до зустрічі',
  },
  // Malay (Bahasa Melayu) - ~30M speakers
  ms: {
    title: 'Libre Bot',
    subtitle: 'Pembantu Dokumentasi AI',
    placeholder: 'Tanya soalan...',
    welcomeMessage: 'Hai! Saya boleh membantu anda mencari jawapan dalam dokumentasi. Apa yang anda ingin tahu?',
    errorPrefix: 'Maaf, berlaku ralat:',
    connectionError: 'Maaf, ada masalah sambungan. Sila cuba lagi.',
    streamingNotSupported: 'Maaf, penstriman tidak disokong.',
    poweredBy: 'Dikuasakan oleh',
    sourcesLabel: 'Sumber:',
    bookingTitle: 'Buat Temujanji',
    selectDate: 'Pilih tarikh',
    selectTime: 'Pilih masa',
    bookAppointment: 'Buat Temujanji',
    noSlotsAvailable: 'Tiada masa tersedia untuk tarikh ini',
    loadingSlots: 'Memuatkan masa tersedia...',
    bookingSuccess: 'Temujanji anda telah disahkan!',
    bookingError: 'Gagal membuat temujanji. Sila cuba lagi.',
    backToChat: 'Kembali ke sembang',
    yourDetails: 'Maklumat Anda',
    confirmBooking: 'Sahkan Tempahan',
    meetingWith: 'Mesyuarat',
    duration: 'Tempoh',
    minutes: 'minit',
    joinMeeting: 'Sertai Mesyuarat',
  },
  // Czech (Čeština) - ~10M speakers
  cs: {
    title: 'Libre Bot',
    subtitle: 'AI asistent dokumentace',
    placeholder: 'Položte otázku...',
    welcomeMessage: 'Ahoj! Mohu vám pomoci najít odpovědi v dokumentaci. Co byste chtěli vědět?',
    errorPrefix: 'Omlouvám se, došlo k chybě:',
    connectionError: 'Omlouvám se, měl jsem problém s připojením. Zkuste to prosím znovu.',
    streamingNotSupported: 'Omlouvám se, streamování není podporováno.',
    poweredBy: 'Využívá technologii',
    sourcesLabel: 'Zdroje:',
    bookingTitle: 'Rezervovat schůzku',
    selectDate: 'Vyberte datum',
    selectTime: 'Vyberte čas',
    bookAppointment: 'Rezervovat schůzku',
    noSlotsAvailable: 'Na tento den nejsou k dispozici žádné termíny',
    loadingSlots: 'Načítání dostupných termínů...',
    bookingSuccess: 'Vaše schůzka byla potvrzena!',
    bookingError: 'Rezervace se nezdařila. Zkuste to prosím znovu.',
    backToChat: 'Zpět na chat',
    yourDetails: 'Vaše údaje',
    confirmBooking: 'Potvrdit rezervaci',
    meetingWith: 'Schůzka',
    duration: 'Délka',
    minutes: 'minut',
    joinMeeting: 'Připojit se ke schůzce',
  },
  // Swedish (Svenska) - ~10M speakers
  sv: {
    title: 'Libre Bot',
    subtitle: 'AI-dokumentationsassistent',
    placeholder: 'Ställ en fråga...',
    welcomeMessage: 'Hej! Jag kan hjälpa dig hitta svar i dokumentationen. Vad vill du veta?',
    errorPrefix: 'Tyvärr uppstod ett fel:',
    connectionError: 'Tyvärr hade jag problem med anslutningen. Försök igen.',
    streamingNotSupported: 'Tyvärr stöds inte streaming.',
    poweredBy: 'Drivs av',
    sourcesLabel: 'Källor:',
    bookingTitle: 'Boka tid',
    selectDate: 'Välj datum',
    selectTime: 'Välj tid',
    bookAppointment: 'Boka tid',
    noSlotsAvailable: 'Inga lediga tider för detta datum',
    loadingSlots: 'Laddar lediga tider...',
    bookingSuccess: 'Din bokning har bekräftats!',
    bookingError: 'Bokningen misslyckades. Försök igen.',
    backToChat: 'Tillbaka till chatten',
    yourDetails: 'Dina uppgifter',
    confirmBooking: 'Bekräfta bokning',
    meetingWith: 'Möte',
    duration: 'Varaktighet',
    minutes: 'minuter',
    joinMeeting: 'Gå med i mötet',
  },
  // Danish (Dansk) - ~6M speakers
  da: {
    title: 'Libre Bot',
    subtitle: 'AI-dokumentationsassistent',
    placeholder: 'Stil et spørgsmål...',
    welcomeMessage: 'Hej! Jeg kan hjælpe dig med at finde svar i dokumentationen. Hvad vil du gerne vide?',
    errorPrefix: 'Beklager, der opstod en fejl:',
    connectionError: 'Beklager, jeg havde problemer med forbindelsen. Prøv venligst igen.',
    streamingNotSupported: 'Beklager, streaming understøttes ikke.',
    poweredBy: 'Drevet af',
    sourcesLabel: 'Kilder:',
    bookingTitle: 'Book en tid',
    selectDate: 'Vælg dato',
    selectTime: 'Vælg tidspunkt',
    bookAppointment: 'Book tid',
    noSlotsAvailable: 'Ingen ledige tider på denne dato',
    loadingSlots: 'Indlæser ledige tider...',
    bookingSuccess: 'Din booking er bekræftet!',
    bookingError: 'Booking mislykkedes. Prøv venligst igen.',
    backToChat: 'Tilbage til chat',
    yourDetails: 'Dine oplysninger',
    confirmBooking: 'Bekræft booking',
    meetingWith: 'Møde',
    duration: 'Varighed',
    minutes: 'minutter',
    joinMeeting: 'Deltag i mødet',
  },
  // Icelandic (Íslenska) - ~350K speakers
  is: {
    title: 'Libre Bot',
    subtitle: 'Gervigreind skjalahjálpari',
    placeholder: 'Spurðu spurningu...',
    welcomeMessage: 'Hæ! Ég get hjálpað þér að finna svör í skjölunum. Hvað viltu vita?',
    errorPrefix: 'Því miður kom upp villa:',
    connectionError: 'Því miður átti ég í vandræðum með tengingu. Vinsamlegast reyndu aftur.',
    streamingNotSupported: 'Því miður er streymi ekki stutt.',
    poweredBy: 'Knúið af',
    sourcesLabel: 'Heimildir:',
    bookingTitle: 'Bóka tíma',
    selectDate: 'Veldu dagsetningu',
    selectTime: 'Veldu tíma',
    bookAppointment: 'Bóka tíma',
    noSlotsAvailable: 'Engir tímar lausir á þessum degi',
    loadingSlots: 'Hleð lausum tímum...',
    bookingSuccess: 'Bókun þín hefur verið staðfest!',
    bookingError: 'Bókun mistókst. Vinsamlegast reyndu aftur.',
    backToChat: 'Til baka í spjall',
    yourDetails: 'Þínar upplýsingar',
    confirmBooking: 'Staðfesta bókun',
    meetingWith: 'Fundur',
    duration: 'Lengd',
    minutes: 'mínútur',
    joinMeeting: 'Taka þátt í fundi',
  },
};

export const RTL_LANGUAGES: SupportedLang[] = ['ar'];

export function detectLanguage(): SupportedLang {
  // 1. Check document's lang attribute
  const htmlLang = document.documentElement.lang?.toLowerCase().split('-')[0];
  if (htmlLang && htmlLang in WIDGET_TRANSLATIONS) {
    return htmlLang as SupportedLang;
  }

  // 2. Check navigator.language
  const navLang = navigator.language?.toLowerCase().split('-')[0];
  if (navLang && navLang in WIDGET_TRANSLATIONS) {
    return navLang as SupportedLang;
  }

  // 3. Default to English
  return 'en';
}
